import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  ListItemText,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import WorkBodyEditor from "./WorkBodyEditor";
import WorkListSidebar from "./WorkListSidebar";
import AspectPreviewDialog from "./AspectPreviewDialog";
import {
  DEFAULT_WORK_BODY_EN,
  DEFAULT_WORK_BODY_NO,
  PRIMARY_CATEGORIES,
  buildWorkCategories,
  buildWorkMarkdown,
  extractImdbId,
  isMeaningfulWorkForm,
  isValidAspect,
  parseWorkMarkdown,
  suggestWorkSlug,
} from "../../Utils/workMarkdown";
import {
  cleanupWorkMuxUpload,
  createWorkMuxUpload,
  deleteWorkDraft,
  fetchWorkDraft,
  fetchWorkDrafts,
  fetchWorkEntries,
  fetchWorkEntry,
  fetchWorkEquipmentCatalog,
  lookupWorkImdbRating,
  pollWorkMuxUpload,
  publishWorkPage,
  saveWorkDraft,
  uploadFileToMux,
} from "../../Utils/workApi";
import {
  getActiveWorkDraftId,
  setActiveWorkDraftId,
} from "../../Utils/workDrafts";
import { ASPECT_PRESETS } from "../../Utils/aspectPreview";

const CATEGORY_LABELS = {
  commercial: "Commercial",
  "feature-film": "Feature film",
  "tv-series": "TV series",
  "music-video": "Music video",
};

const emptyClient = () => ({ name: "", url: "" });

const AddWorkForm = () => {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [slug, setSlug] = useState("");
  const [slugLocked, setSlugLocked] = useState(false);
  const [clients, setClients] = useState([emptyClient()]);
  const [imdbLink, setImdbLink] = useState("");
  const [imdbRating, setImdbRating] = useState(null);
  const [imdbStatus, setImdbStatus] = useState("");
  const [loadedSlug, setLoadedSlug] = useState("");
  const [editorNonce, setEditorNonce] = useState(0);
  const [workEntries, setWorkEntries] = useState([]);
  const [workListError, setWorkListError] = useState("");
  const [workListLoading, setWorkListLoading] = useState(true);
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [aspectPreset, setAspectPreset] = useState("");
  const [aspectCustom, setAspectCustom] = useState("");
  const [primaryCategory, setPrimaryCategory] = useState("");
  const [featured, setFeatured] = useState(false);
  const [showreel, setShowreel] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState("");
  const [catalogReady, setCatalogReady] = useState(false);
  const [equipmentIds, setEquipmentIds] = useState([]);
  const [service, setService] = useState("");
  const [serviceLocked, setServiceLocked] = useState(false);
  const [contentEn, setContentEn] = useState(DEFAULT_WORK_BODY_EN);
  const [contentNo, setContentNo] = useState(DEFAULT_WORK_BODY_NO);
  const [muxPlaybackId, setMuxPlaybackId] = useState("");
  const [muxAssetId, setMuxAssetId] = useState("");
  const [muxDuration, setMuxDuration] = useState(null);
  const [protectedMuxAssetId, setProtectedMuxAssetId] = useState("");
  const [aspectPreviewOpen, setAspectPreviewOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishError, setPublishError] = useState("");
  const [publishResult, setPublishResult] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [activeDraftId, setActiveDraftIdState] = useState("");
  const [draftStatus, setDraftStatus] = useState("");
  const fileInputRef = useRef(null);
  const replaceConfirmedRef = useRef(false);
  const skipDraftSaveRef = useRef(true);
  const snapshotRef = useRef(null);
  const activeDraftIdRef = useRef("");
  const persistDraftRef = useRef(async () => null);

  const setActiveDraft = (id) => {
    activeDraftIdRef.current = id || "";
    setActiveDraftIdState(id || "");
    setActiveWorkDraftId(id || "");
  };

  useEffect(() => {
    let cancelled = false;
    fetchWorkEquipmentCatalog()
      .then((items) => {
        if (!cancelled) setCatalog(Array.isArray(items) ? items : []);
      })
      .catch((error) => {
        if (!cancelled) {
          setCatalogError(error.message || "Could not load equipment.");
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogReady(true);
      });
    fetchWorkEntries()
      .then((entries) => {
        if (!cancelled) setWorkEntries(Array.isArray(entries) ? entries : []);
      })
      .catch((error) => {
        if (!cancelled) {
          setWorkListError(error.message || "Could not load existing work.");
        }
      })
      .finally(() => {
        if (!cancelled) setWorkListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (slugLocked) return;
    setSlug(suggestWorkSlug({ date, clients, title }));
  }, [date, clients, title, slugLocked]);

  const selectedEquipment = useMemo(
    () => catalog.filter((item) => equipmentIds.includes(item.id)),
    [catalog, equipmentIds],
  );

  useEffect(() => {
    if (serviceLocked) return;
    setService(selectedEquipment.map((item) => item.en).join(" / "));
  }, [selectedEquipment, serviceLocked]);

  const aspect =
    aspectPreset === "custom" ? aspectCustom.trim() : aspectPreset;
  const categories = buildWorkCategories({
    primary: primaryCategory,
    featured,
    showreel,
  });
  const markdown = buildWorkMarkdown({
    title,
    clients,
    date,
    imdbLink,
    imdbRating,
    aspect,
    categories,
    service,
    equipment: equipmentIds,
    muxPlaybackId,
    muxAssetId,
    contentEn,
    contentNo,
  });

  const groupedEquipment = useMemo(() => {
    return catalog.reduce((groups, item) => {
      const key = item.serviceSlug || "other";
      if (!groups[key]) {
        groups[key] = {
          label: item.serviceEn || key,
          items: [],
        };
      }
      groups[key].items.push(item);
      return groups;
    }, {});
  }, [catalog]);

  const draftSnapshot = useMemo(
    () => ({
      id: activeDraftId,
      title,
      date,
      slug,
      slugLocked,
      loadedSlug,
      clients,
      imdbLink,
      imdbRating,
      aspectPreset,
      aspectCustom,
      primaryCategory,
      featured,
      showreel,
      equipmentIds,
      service,
      serviceLocked,
      contentEn,
      contentNo,
      muxPlaybackId,
      muxAssetId,
      muxDuration,
      protectedMuxAssetId,
    }),
    [
      activeDraftId,
      title,
      date,
      slug,
      slugLocked,
      loadedSlug,
      clients,
      imdbLink,
      imdbRating,
      aspectPreset,
      aspectCustom,
      primaryCategory,
      featured,
      showreel,
      equipmentIds,
      service,
      serviceLocked,
      contentEn,
      contentNo,
      muxPlaybackId,
      muxAssetId,
      muxDuration,
      protectedMuxAssetId,
    ],
  );
  snapshotRef.current = draftSnapshot;

  const refreshDrafts = async () => {
    try {
      const items = await fetchWorkDrafts();
      setDrafts(Array.isArray(items) ? items : []);
    } catch (error) {
      setWorkListError(error.message || "Could not load drafts.");
    }
  };

  const persistDraft = async (overrides = {}, options = {}) => {
    const snapshot = { ...(snapshotRef.current || {}), ...overrides };
    if (!isMeaningfulWorkForm(snapshot)) return null;
    const saved = await saveWorkDraft(snapshot);
    if (saved?.id && saved.id !== activeDraftIdRef.current) {
      setActiveDraft(saved.id);
    }
    setDraftStatus("Draft saved.");
    if (options.refresh !== false) {
      await refreshDrafts();
    }
    return saved;
  };
  persistDraftRef.current = persistDraft;

  const applyDraft = (draft) => {
    skipDraftSaveRef.current = true;
    setTitle(draft.title || "");
    setDate(draft.date || dayjs().format("YYYY-MM-DD"));
    setSlug(draft.slug || "");
    setSlugLocked(Boolean(draft.slugLocked || draft.loadedSlug));
    setLoadedSlug(draft.loadedSlug || "");
    setClients(draft.clients?.length ? draft.clients : [emptyClient()]);
    setImdbLink(draft.imdbLink || "");
    setImdbRating(draft.imdbRating ?? null);
    setImdbStatus("");
    setAspectPreset(draft.aspectPreset || "");
    setAspectCustom(draft.aspectCustom || "");
    setPrimaryCategory(
      PRIMARY_CATEGORIES.includes(draft.primaryCategory) ?
        draft.primaryCategory :
        "",
    );
    setFeatured(Boolean(draft.featured));
    setShowreel(Boolean(draft.showreel));
    setEquipmentIds(draft.equipmentIds || []);
    setService(draft.service || "");
    setServiceLocked(Boolean(draft.serviceLocked));
    setContentEn(draft.contentEn || DEFAULT_WORK_BODY_EN);
    setContentNo(draft.contentNo || DEFAULT_WORK_BODY_NO);
    setMuxPlaybackId(draft.muxPlaybackId || "");
    setMuxAssetId(draft.muxAssetId || "");
    setMuxDuration(draft.muxDuration > 0 ? draft.muxDuration : null);
    setProtectedMuxAssetId(
      draft.protectedMuxAssetId ||
        (draft.loadedSlug ? draft.muxAssetId || "" : ""),
    );
    setEditorNonce((value) => value + 1);
    setPublishError("");
    setPublishResult(null);
    setUploadStatus(draft.muxPlaybackId ? "Loaded existing Mux ids." : "");
    setActiveDraft(draft.id || "");
    setTimeout(() => {
      skipDraftSaveRef.current = false;
    }, 0);
  };

  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      try {
        const items = await fetchWorkDrafts();
        if (cancelled) return;
        setDrafts(Array.isArray(items) ? items : []);
        const remembered = getActiveWorkDraftId();
        if (remembered && items.some((item) => item.id === remembered)) {
          const draft = await fetchWorkDraft(remembered);
          if (!cancelled) applyDraft(draft);
        }
      } catch (error) {
        if (!cancelled) {
          setWorkListError(error.message || "Could not load drafts.");
        }
      } finally {
        skipDraftSaveRef.current = false;
      }
    };
    restore();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const flush = () => {
      if (skipDraftSaveRef.current) return;
      const snapshot = snapshotRef.current || {};
      if (!isMeaningfulWorkForm(snapshot)) return;
      persistDraftRef.current({}, { refresh: false }).catch(() => {});
    };
    const onHidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    window.addEventListener("freeze", flush);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      flush();
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("freeze", flush);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, []);

  const updateClient = (index, field, value) => {
    setClients((current) =>
      current.map((client, clientIndex) =>
        clientIndex === index ? { ...client, [field]: value } : client,
      ),
    );
  };

  const applyParsedWork = (parsed) => {
    setLoadedSlug(parsed.slug || "");
    setTitle(parsed.title || "");
    setDate(parsed.date || dayjs().format("YYYY-MM-DD"));
    setSlug(parsed.slug || "");
    setSlugLocked(true);
    setClients(parsed.clients?.length ? parsed.clients : [emptyClient()]);
    setImdbLink(parsed.imdbLink || "");
    setImdbRating(parsed.imdbRating ?? null);
    setImdbStatus("");
    if (parsed.aspect && ASPECT_PRESETS.includes(parsed.aspect)) {
      setAspectPreset(parsed.aspect);
      setAspectCustom("");
    } else if (parsed.aspect) {
      setAspectPreset("custom");
      setAspectCustom(parsed.aspect);
    } else {
      setAspectPreset("");
      setAspectCustom("");
    }
    setPrimaryCategory(
      PRIMARY_CATEGORIES.includes(parsed.primary) ? parsed.primary : "",
    );
    setFeatured(Boolean(parsed.featured));
    setShowreel(Boolean(parsed.showreel));
    setEquipmentIds(parsed.equipment || []);
    setService(parsed.service || "");
    setServiceLocked(true);
    setContentEn(parsed.contentEn || "");
    setContentNo(parsed.contentNo || "");
    setMuxPlaybackId(parsed.muxPlaybackId || "");
    setMuxAssetId(parsed.muxAssetId || "");
    setMuxDuration(null);
    setProtectedMuxAssetId(
      parsed.slug && parsed.muxAssetId ? parsed.muxAssetId : "",
    );
    setEditorNonce((value) => value + 1);
    setPublishError("");
    setPublishResult(null);
    setUploadStatus(parsed.muxPlaybackId ? "Loaded existing Mux ids." : "");
  };

  const resetForm = async ({ skipPersist = false } = {}) => {
    if (!skipPersist && isMeaningfulWorkForm(snapshotRef.current)) {
      try {
        await persistDraft();
      } catch (error) {
        setPublishError(error.message || "Could not save the current draft.");
      }
    }
    skipDraftSaveRef.current = true;
    applyParsedWork({
      slug: "",
      title: "",
      date: dayjs().format("YYYY-MM-DD"),
      clients: [emptyClient()],
      imdbLink: "",
      imdbRating: null,
      aspect: "",
      primary: "",
      featured: false,
      showreel: false,
      equipment: [],
      service: "",
      contentEn: DEFAULT_WORK_BODY_EN,
      contentNo: DEFAULT_WORK_BODY_NO,
      muxPlaybackId: "",
      muxAssetId: "",
    });
    setLoadedSlug("");
    setSlugLocked(false);
    setServiceLocked(false);
    setSlug(suggestWorkSlug({
      date: dayjs().format("YYYY-MM-DD"),
      clients: [emptyClient()],
      title: "",
    }));
    setActiveDraft("");
    setDraftStatus("");
    setTimeout(() => {
      skipDraftSaveRef.current = false;
    }, 0);
  };

  const handleSelectWork = async (slugToLoad) => {
    setLoadingEntry(true);
    setPublishError("");
    try {
      if (isMeaningfulWorkForm(snapshotRef.current)) {
        await persistDraft();
      }
      const entry = await fetchWorkEntry(slugToLoad);
      skipDraftSaveRef.current = true;
      applyParsedWork(parseWorkMarkdown(entry.markdown, entry.slug || slugToLoad));
      setActiveDraft("");
      setTimeout(() => {
        skipDraftSaveRef.current = false;
      }, 0);
    } catch (error) {
      setPublishError(error.message || "Could not load that work file.");
    } finally {
      setLoadingEntry(false);
    }
  };

  const handleSelectDraft = async (id) => {
    setLoadingEntry(true);
    setPublishError("");
    try {
      if (isMeaningfulWorkForm(snapshotRef.current) && activeDraftIdRef.current !== id) {
        await persistDraft();
      }
      const draft = await fetchWorkDraft(id);
      applyDraft(draft);
    } catch (error) {
      setPublishError(error.message || "Could not load that draft.");
    } finally {
      setLoadingEntry(false);
    }
  };

  const handleDiscardDraft = async (draft) => {
    const label = draft.title || draft.slug || "this draft";
    const removesVideo = Boolean(draft.muxAssetId) && !draft.loadedSlug;
    const confirmed = window.confirm(
      removesVideo
        ? `Discard "${label}" for everyone and delete the Mux video?`
        : `Discard "${label}" for everyone? The published video is kept.`,
    );
    if (!confirmed) return;
    setLoadingEntry(true);
    setPublishError("");
    try {
      await deleteWorkDraft({
        id: draft.id,
        keepVideo: Boolean(draft.loadedSlug),
      });
      if (activeDraftIdRef.current === draft.id) {
        await resetForm({ skipPersist: true });
      }
      await refreshDrafts();
    } catch (error) {
      setPublishError(error.message || "Could not discard that draft.");
    } finally {
      setLoadingEntry(false);
    }
  };

  const handleImdbBlur = async () => {
    const id = extractImdbId(imdbLink);
    if (!id) {
      setImdbStatus(imdbLink.trim() ? "Enter a full IMDb title URL." : "");
      return;
    }
    setImdbStatus("Looking up rating…");
    try {
      const result = await lookupWorkImdbRating(imdbLink);
      if (result.imdbRating != null) {
        setImdbRating(result.imdbRating);
      }
      if (!title.trim() && result.title) {
        setTitle(result.title);
      }
      setImdbStatus(
        result.imdbRating != null
          ? `IMDb rating ${result.imdbRating}`
          : "No rating found. Enter it manually if you have it.",
      );
    } catch (error) {
      setImdbStatus(error.message || "Could not look up IMDb.");
    }
  };

  const confirmReplaceVideo = () => {
    const currentAsset = muxAssetId.trim();
    const publishedAsset = protectedMuxAssetId.trim();
    if (!currentAsset && !muxPlaybackId.trim()) return true;
    if (currentAsset && publishedAsset && currentAsset === publishedAsset) {
      return window.confirm(
        "Upload a new video for this draft? The video already on the website stays in Mux.",
      );
    }
    if (!currentAsset) {
      return window.confirm(
        "Replace the current video? Its Mux asset id is missing, so the previous upload cannot be deleted automatically.",
      );
    }
    return window.confirm(
      "Replace the current video? The previous Mux upload will be deleted.",
    );
  };

  const waitForMuxReady = async (uploadId) => {
    const started = Date.now();
    while (Date.now() - started < 8 * 60 * 1000) {
      const status = await pollWorkMuxUpload(uploadId);
      if (status.status === "ready") return status;
      if (status.status === "errored") {
        const failure = new Error(status.error || "Mux encoding failed.");
        failure.muxAssetId = status.assetId || "";
        throw failure;
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
    throw new Error("Mux encoding timed out.");
  };

  const removeMuxUpload = async ({ uploadId = "", assetId = "" } = {}) => {
    if (!uploadId && !assetId) return;
    await cleanupWorkMuxUpload({ uploadId, assetId });
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const previousAssetId = muxAssetId.trim();
    const hasVideo = Boolean(previousAssetId || muxPlaybackId.trim());
    const confirmed = replaceConfirmedRef.current;
    replaceConfirmedRef.current = false;
    if (!PRIMARY_CATEGORIES.includes(primaryCategory)) {
      setUploadStatus(
        "Choose a primary category before uploading. Mux stores it as the creator id.",
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (hasVideo && !confirmed && !confirmReplaceVideo()) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus("Creating Mux upload…");
    setPublishError("");
    let newUploadId = "";
    let saved = false;
    try {
      const { uploadUrl, uploadId } = await createWorkMuxUpload({
        corsOrigin: window.location.origin,
        slug,
        title,
        category: primaryCategory,
      });
      newUploadId = uploadId;
      setUploadStatus("Uploading video…");
      await uploadFileToMux(uploadUrl, file, setUploadProgress);
      setUploadStatus("Waiting for Mux to encode…");
      const ready = await waitForMuxReady(uploadId);
      await persistDraft({
        muxPlaybackId: ready.playbackId,
        muxAssetId: ready.assetId,
        muxDuration: ready.duration > 0 ? ready.duration : null,
      });
      saved = true;
      setMuxPlaybackId(ready.playbackId);
      setMuxAssetId(ready.assetId);
      setMuxDuration(ready.duration > 0 ? ready.duration : null);
      const publishedAsset = protectedMuxAssetId.trim();
      const shouldDeletePrevious =
        previousAssetId &&
        previousAssetId !== ready.assetId &&
        previousAssetId !== publishedAsset;
      if (shouldDeletePrevious) {
        try {
          await removeMuxUpload({ assetId: previousAssetId });
          setUploadStatus("Video is ready. The previous upload was deleted.");
        } catch (deleteError) {
          setUploadStatus(
            "Video is ready, but the previous Mux upload could not be deleted.",
          );
        }
      } else {
        setUploadStatus(
          publishedAsset && previousAssetId === publishedAsset
            ? "Video is ready. The published video was kept."
            : "Video is ready. Draft saved.",
        );
      }
    } catch (error) {
      if (!saved && (newUploadId || error.muxAssetId)) {
        try {
          await removeMuxUpload({
            uploadId: newUploadId,
            assetId: error.muxAssetId || "",
          });
          setUploadStatus(
            `${error.message || "Video upload failed."} The failed upload was removed.`,
          );
        } catch (cleanupError) {
          setUploadStatus(
            `${error.message || "Video upload failed."} The failed upload is still in Mux.`,
          );
        }
      } else {
        setUploadStatus(error.message || "Video upload failed.");
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSaveDraft = async () => {
    setPublishError("");
    if (!isMeaningfulWorkForm(snapshotRef.current)) {
      setPublishError(
        "Add a title, video, or body edits before saving a draft.",
      );
      return;
    }
    setSavingDraft(true);
    try {
      await persistDraft();
    } catch (error) {
      setPublishError(error.message || "Could not save the draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    setPublishError("");
    setPublishResult(null);
    if (!PRIMARY_CATEGORIES.includes(primaryCategory)) {
      setPublishError("Primary category is required.");
      return;
    }
    if (!title.trim()) {
      setPublishError("Title is required.");
      return;
    }
    if (!slug.trim()) {
      setPublishError("Slug is required.");
      return;
    }
    if (!muxPlaybackId || !muxAssetId) {
      setPublishError("Upload a video to Mux before publishing.");
      return;
    }
    if (aspect && !isValidAspect(aspect)) {
      setPublishError("Aspect must look like 2.39:1.");
      return;
    }
    if (!contentEn.trim()) {
      const proceed = window.confirm(
        "English body is empty. Publish anyway?",
      );
      if (!proceed) return;
    }

    setPublishing(true);
    try {
      const result = await publishWorkPage({
        slug: slug.trim(),
        title: title.trim(),
        markdown,
        muxPlaybackId,
        muxAssetId,
        category: primaryCategory,
        imdbLink,
      });
      setPublishResult(result);
      if (activeDraftIdRef.current) {
        await deleteWorkDraft({
          id: activeDraftIdRef.current,
          keepVideo: true,
        });
        setActiveDraft("");
        await refreshDrafts();
      }
    } catch (error) {
      setPublishError(error.message || "Could not open a pull request.");
    } finally {
      setPublishing(false);
    }
  };

  const hasPrimaryCategory = PRIMARY_CATEGORIES.includes(primaryCategory);
  const uploadHint = hasPrimaryCategory
    ? ""
    : "Choose a primary category first. Mux stores it as the video creator id.";

  const aspectPreviewHint = uploading
    ? "Video is still uploading. Preview opens once Mux has finished."
    : muxPlaybackId.trim()
      ? ""
      : "Upload a video first. Preview uses frames from that video.";

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "stretch",
          flexDirection: { xs: "column", md: "row" },
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
      <Box sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}>
        {loadingEntry && <LinearProgress />}
        {loadedSlug && (
          <Alert severity="info">
            Editing {loadedSlug}. Publish project updates that page on the website.
          </Alert>
        )}
        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          fullWidth
          required
        />

        <DatePicker
          label="Date"
          value={date ? dayjs(date) : null}
          onChange={(value) =>
            setDate(value ? value.format("YYYY-MM-DD") : "")
          }
          slotProps={{ textField: { fullWidth: true } }}
        />

        <Box>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Clients</Typography>
          {clients.map((client, index) => (
            <Box
              key={index}
              sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}
            >
              <TextField
                label="Name"
                value={client.name}
                onChange={(event) =>
                  updateClient(index, "name", event.target.value)
                }
                sx={{ flex: 1 }}
              />
              <TextField
                label="Website"
                value={client.url}
                onChange={(event) =>
                  updateClient(index, "url", event.target.value)
                }
                sx={{ flex: 1.4 }}
              />
              <IconButton
                aria-label="Remove client"
                onClick={() =>
                  setClients((current) =>
                    current.length === 1
                      ? [emptyClient()]
                      : current.filter((_, clientIndex) => clientIndex !== index),
                  )
                }
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => setClients((current) => [...current, emptyClient()])}
          >
            Add client
          </Button>
        </Box>

        <TextField
          label="Slug"
          value={slug}
          onChange={(event) => {
            setSlugLocked(true);
            setSlug(event.target.value);
          }}
          disabled={Boolean(loadedSlug)}
          helperText={
            loadedSlug
              ? "Locked while editing this page"
              : "Folder name under src/content/work"
          }
          fullWidth
        />

        <FormControl required>
          <FormLabel>Primary category</FormLabel>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Required before upload. Mux stores this as the video creator id.
          </Typography>
          <RadioGroup
            value={primaryCategory}
            onChange={(event) => setPrimaryCategory(event.target.value)}
          >
            {PRIMARY_CATEGORIES.map((category) => (
              <FormControlLabel
                key={category}
                value={category}
                control={<Radio />}
                label={CATEGORY_LABELS[category]}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <Box>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Video</Typography>
          <Tooltip
            title={uploadHint}
            disableHoverListener={!uploadHint}
            disableFocusListener={!uploadHint}
          >
            <span
              style={{
                display: "inline-flex",
                cursor: uploadHint ? "not-allowed" : undefined,
              }}
            >
              <Button
                variant="outlined"
                component="label"
                disabled={uploading || !hasPrimaryCategory}
                onClick={(event) => {
                  if (!muxAssetId.trim() && !muxPlaybackId.trim()) return;
                  replaceConfirmedRef.current = false;
                  if (!confirmReplaceVideo()) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                  }
                  replaceConfirmedRef.current = true;
                }}
              >
                {uploading ? "Uploading…" : "Upload video to Mux"}
                {hasPrimaryCategory && (
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                  />
                )}
              </Button>
            </span>
          </Tooltip>
          {uploading && (
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ mt: 1 }}
            />
          )}
          {uploadStatus && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {uploadStatus}
            </Typography>
          )}
          <TextField
            label="Mux playback id"
            value={muxPlaybackId}
            onChange={(event) => {
              setMuxPlaybackId(event.target.value);
              setMuxDuration(null);
            }}
            fullWidth
            sx={{ mt: 2 }}
          />
          <TextField
            label="Mux asset id"
            value={muxAssetId}
            onChange={(event) => setMuxAssetId(event.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: "column", sm: "row" } }}>
          <TextField
            label="IMDb URL"
            value={imdbLink}
            onChange={(event) => setImdbLink(event.target.value)}
            onBlur={handleImdbBlur}
            helperText={imdbStatus || "Optional. Rating can be filled in by hand."}
            fullWidth
          />
          <TextField
            label="IMDb rating"
            type="number"
            value={imdbRating ?? ""}
            onChange={(event) => {
              const next = event.target.value;
              if (next === "") {
                setImdbRating(null);
                return;
              }
              const parsed = Number(next);
              setImdbRating(Number.isFinite(parsed) ? parsed : null);
            }}
            inputProps={{ min: 1, max: 10, step: 0.1 }}
            helperText="1.0 to 10.0"
            sx={{ width: { xs: "100%", sm: 160 }, flexShrink: 0 }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="aspect-label">Aspect</InputLabel>
            <Select
              labelId="aspect-label"
              label="Aspect"
              value={aspectPreset}
              onChange={(event) => setAspectPreset(event.target.value)}
            >
              <MenuItem value="">Leave empty (16:9 fallback)</MenuItem>
              {ASPECT_PRESETS.filter(Boolean).map((preset) => (
                <MenuItem key={preset} value={preset}>
                  {preset === "custom" ? "Custom" : preset}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip
            title={aspectPreviewHint}
            disableHoverListener={!aspectPreviewHint}
            disableFocusListener={!aspectPreviewHint}
          >
            <span
              style={{
                display: "inline-flex",
                cursor: aspectPreviewHint ? "not-allowed" : undefined,
              }}
            >
              <Button
                variant="outlined"
                disabled={Boolean(aspectPreviewHint)}
                onClick={() => setAspectPreviewOpen(true)}
                sx={{ height: 56, whiteSpace: "nowrap" }}
              >
                Preview aspect
              </Button>
            </span>
          </Tooltip>
        </Box>
        {aspectPreset === "custom" && (
          <TextField
            label="Custom aspect"
            value={aspectCustom}
            onChange={(event) => setAspectCustom(event.target.value)}
            placeholder="2.41:1"
            fullWidth
          />
        )}
        <AspectPreviewDialog
          open={aspectPreviewOpen}
          onClose={() => setAspectPreviewOpen(false)}
          playbackId={muxPlaybackId.trim()}
          duration={muxDuration}
          onDuration={setMuxDuration}
          aspectPreset={aspectPreset}
          aspectCustom={aspectCustom}
          onPresetChange={setAspectPreset}
          onCustomChange={setAspectCustom}
        />

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={featured}
                onChange={(event) => setFeatured(event.target.checked)}
              />
            }
            label="Featured"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showreel}
                onChange={(event) => setShowreel(event.target.checked)}
              />
            }
            label="Showreel"
          />
        </FormGroup>

        <Box>
          {catalogError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {catalogError}
            </Alert>
          )}
          <FormControl fullWidth>
            <InputLabel id="equipment-label">Equipment</InputLabel>
            <Select
              labelId="equipment-label"
              label="Equipment"
              multiple
              value={equipmentIds}
              onChange={(event) => {
                const next = event.target.value;
                setEquipmentIds(typeof next === "string" ? next.split(",") : next);
              }}
              disabled={!catalogReady || catalog.length === 0}
              renderValue={(selected) =>
                selected
                  .map((id) => catalog.find((item) => item.id === id)?.en || id)
                  .join(", ")
              }
              MenuProps={{
                autoFocus: false,
                PaperProps: { sx: { maxHeight: 360 } },
              }}
            >
              {Object.entries(groupedEquipment).flatMap(([key, group]) => [
                <ListSubheader
                  key={`${key}-head`}
                  disableSticky
                  sx={{ color: "#3598b7", fontWeight: 600, lineHeight: "32px" }}
                >
                  {group.label}
                </ListSubheader>,
                ...group.items.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Checkbox
                      size="small"
                      checked={equipmentIds.includes(item.id)}
                    />
                    <ListItemText
                      primary={
                        item.no && item.no !== item.en
                          ? `${item.en} / ${item.no}`
                          : item.en
                      }
                    />
                  </MenuItem>
                )),
              ])}
              {catalogReady && catalog.length === 0 && (
                <MenuItem disabled value="">
                  No equipment loaded
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>

        <TextField
          label="Service"
          value={service}
          onChange={(event) => {
            setServiceLocked(true);
            setService(event.target.value);
          }}
          helperText="Prefills from selected equipment. Edit freely."
          fullWidth
        />

        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>English body</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            Starter text follows existing work pages. Replace the copy and use
            Link equipment for real kit names.
          </Typography>
          <WorkBodyEditor
            key={`${editorNonce}-en`}
            locale="en"
            catalog={catalog}
            catalogReady={catalogReady}
            selectedEquipmentIds={equipmentIds}
            initialMarkdown={contentEn}
            onChange={setContentEn}
            placeholder="English project description"
          />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Norwegian body</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            Same structure in Norwegian, including equipment links.
          </Typography>
          <WorkBodyEditor
            key={`${editorNonce}-no`}
            locale="no"
            catalog={catalog}
            catalogReady={catalogReady}
            selectedEquipmentIds={equipmentIds}
            initialMarkdown={contentNo}
            onChange={setContentNo}
            placeholder="Norsk prosjektbeskrivelse"
          />
        </Box>

        {publishError && <Alert severity="error">{publishError}</Alert>}
        {publishResult?.prUrl && (
          <Alert severity="success">
            Published to the website:{" "}
            <Link href={publishResult.prUrl} target="_blank" rel="noopener noreferrer">
              {publishResult.prUrl}
            </Link>
            {publishResult.warnings?.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                {publishResult.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {draftStatus && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {draftStatus}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={savingDraft || publishing || uploading}
          >
            {savingDraft ? <CircularProgress size={22} /> : "Save draft"}
          </Button>
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={publishing || uploading}
          >
            {publishing ? <CircularProgress size={22} /> : "Publish project"}
          </Button>
        </Box>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>Preview markdown</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                overflowX: "auto",
                backgroundColor: "#f6f6f6",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.8rem",
                whiteSpace: "pre-wrap",
              }}
            >
              {markdown}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Box sx={{
        width: { xs: "100%", md: 320 },
        flex: { xs: "1 1 0", md: "0 0 320px" },
        minHeight: 0,
        height: { md: "100%" },
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}>
        <WorkListSidebar
          entries={workEntries}
          drafts={drafts}
          loading={workListLoading}
          error={workListError}
          selectedSlug={loadedSlug}
          selectedDraftId={activeDraftId}
          onSelect={handleSelectWork}
          onSelectDraft={handleSelectDraft}
          onDiscardDraft={handleDiscardDraft}
          onNew={resetForm}
        />
      </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AddWorkForm;
